
import React, { useState, useEffect } from 'react';
import { Users, Clock, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useTimetableData } from '../../hooks/useTimetableData';

const TeacherAvailability = ({ timetableData = [] }) => {
  const [teacherStatus, setTeacherStatus] = useState([]);
  const { refreshData } = useTimetableData();
  const [refreshing, setRefreshing] = useState(false);
  
  const updateTeacherStatus = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      setTeacherStatus([]);
      return;
    }
    
    const now = new Date();
    
    // Calculate teacher availability based on current time and scheduled classes
    const teacherStatusData = data.map(teacher => {
      if (!teacher.classes || !Array.isArray(teacher.classes)) {
        return {
          id: teacher.id,
          name: teacher.name,
          status: 'available',
          nextClass: null
        };
      }
      
      // Find current or upcoming class for this teacher
      let currentClass = null;
      let nextClass = null;
      
      for (const classItem of teacher.classes) {
        if (!classItem || !classItem.startTime || !classItem.endTime) continue;
        
        try {
          const startTime = new Date(classItem.startTime);
          const endTime = new Date(classItem.endTime);
          
          // If currently in a class
          if (now >= startTime && now <= endTime) {
            currentClass = classItem;
            break;
          }
          
          // Find next upcoming class
          if (startTime > now) {
            if (!nextClass || startTime < new Date(nextClass.startTime)) {
              nextClass = classItem;
            }
          }
        } catch (error) {
          console.error('Error parsing date:', error, classItem);
        }
      }
      
      let status = 'available';
      if (currentClass) {
        status = currentClass.status === 'absent' ? 'absent' : 'teaching';
      } else if (teacher.classes.some(c => c.status === 'absent' && new Date(c.startTime) < now && new Date(c.endTime) > now)) {
        status = 'absent';
      }
      
      return {
        id: teacher.id,
        name: teacher.name,
        status,
        currentClass,
        nextClass
      };
    });
    
    setTeacherStatus(teacherStatusData);
  };
  
  useEffect(() => {
    // Update teacher status whenever timetable data changes
    updateTeacherStatus(timetableData);
    
    // Set up interval to refresh status every minute for real-time updates
    const intervalId = setInterval(() => {
      updateTeacherStatus(timetableData);
    }, 60000);
    
    return () => clearInterval(intervalId);
  }, [timetableData]);
  
  const handleRefresh = async () => {
    // Prevent multiple clicks by checking if already refreshing
    if (refreshing) return;
    
    try {
      setRefreshing(true);
      await refreshData();
      toast.success('Teacher availability updated');
    } catch (error) {
      // Only show error if it's not the debounce error
      if (error instanceof Error && !error.message.includes('Please wait before refreshing again')) {
        toast.error('Failed to update availability');
      }
    } finally {
      setRefreshing(false);
    }
  };
  
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center">
          <Users className="mr-2 h-5 w-5" />
          Teacher Availability
        </CardTitle>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleRefresh} 
          disabled={refreshing}
          className="h-8 w-8"
          title="Refresh availability"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {teacherStatus.length > 0 ? (
          <div className="space-y-2">
            {teacherStatus.map((teacher) => (
              <div key={teacher.id} className="flex justify-between items-center p-2 border rounded">
                <div>
                  <div className="font-medium">{teacher.name}</div>
                  {teacher.currentClass && (
                    <div className="text-xs text-muted-foreground">
                      Currently: {teacher.currentClass.subject}
                    </div>
                  )}
                  {teacher.nextClass && !teacher.currentClass && (
                    <div className="text-xs flex items-center text-muted-foreground">
                      <Clock className="h-3 w-3 mr-1" />
                      Next: {teacher.nextClass.subject} at {new Date(teacher.nextClass.startTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </div>
                  )}
                </div>
                <Badge 
                  variant={teacher.status === 'available' ? "outline" : "secondary"}
                  className={`
                    ${teacher.status === 'available' ? 'border-green-500 text-green-800 bg-green-50' : 
                      teacher.status === 'teaching' ? 'bg-blue-100 text-blue-800' : 
                      'bg-red-100 text-red-800'}
                  `}
                >
                  {teacher.status === 'available' ? 'AVAILABLE' : 
                   teacher.status === 'teaching' ? 'TEACHING' : 
                   'ABSENT'}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <p>No teacher availability data</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeacherAvailability;
